/**
 * StackedBarChart: one bar per category, one segment per series, drawn from plain
 * data with plain props (ADR-026) on Recharts. Category labels render under the
 * groups, the legend carries token swatches, every segment names itself, and the
 * click-through contract survives through Recharts' own click events.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { strings } from "../brand/strings";

import { ChartEmpty, CHART_MARGIN, Legend, seriesClass, seriesColor, surfaceClass } from "./shared";

export interface StackSeries {
  readonly key: string;
  readonly label: string;
}

export interface StackedBarDatum {
  readonly label: string;
  readonly segments: readonly { readonly key: string; readonly value: number }[];
}

interface StackShapeProps {
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly height?: number;
  readonly index?: number;
}

export function StackedBarChart({
  ariaLabel,
  series,
  data,
  onSelectSegment,
  tickInterval = 0,
  xAxisLabel,
}: {
  ariaLabel: string;
  /**
   * Wave W2: the category tick strategy. 0 (the default) draws every label;
   * "preserveStartEnd" lets the axis drop the labels that would collide on a narrow
   * frame (twelve months at 390px) instead of printing them over each other.
   */
  tickInterval?: number | "preserveStart" | "preserveEnd" | "preserveStartEnd";
  /** The category axis title; the generic word when absent. */
  xAxisLabel?: string;
  /** The series, in stacking order bottom to top, keyed to the segments. */
  series: readonly StackSeries[];
  data: readonly StackedBarDatum[];
  /**
   * Optional click-through (the chart contract): one callback per segment. Only
   * wired when the segment has a natural filtered register; never a dead point.
   */
  onSelectSegment?: (seriesKey: string, datum: StackedBarDatum) => void;
}): React.JSX.Element {
  const total = data.reduce(
    (sum, datum) => sum + datum.segments.reduce((inner, segment) => inner + segment.value, 0),
    0,
  );
  if (data.length === 0 || series.length === 0 || total === 0) {
    return <ChartEmpty />;
  }
  const rows = data.map((datum) => {
    const row: Record<string, string | number> = { label: datum.label };
    for (const segment of datum.segments) {
      row[segment.key] = segment.value;
    }
    return row;
  });

  return (
    <figure className="q-chart" data-testid="stacked-bar-chart">
      <div className="q-chart__frame">
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 640, height: 320 }}
        >
          <RechartsBarChart
            data={rows}
            margin={CHART_MARGIN}
            role="img"
            aria-label={ariaLabel}
            ref={surfaceClass}
          >
            <CartesianGrid stroke="var(--q-rule)" vertical={false} />
            <XAxis
              dataKey="label"
              interval={tickInterval}
              tickLine={false}
              axisLine={{ stroke: "var(--q-rule-strong)" }}
              label={{
                value: xAxisLabel ?? strings.charts.categoryAxis,
                position: "insideBottom",
                offset: -2,
              }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number): string => String(Math.round(value))}
              label={{
                value: strings.charts.valueAxis,
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle" },
              }}
            />
            <Tooltip
              isAnimationActive={false}
              cursor={{ fill: "var(--q-sig-wash)" }}
              content={({ active: hover, payload, label }) =>
                hover && payload.length > 0 ? (
                  <div className="q-chart__tooltip">
                    <p className="q-chart__tooltip-title">{String(label)}</p>
                    <ul className="q-chart__tooltip-list">
                      {payload.map((entry, index) => (
                        <li key={index} className="q-chart__tooltip-row">
                          <span
                            aria-hidden="true"
                            className="q-chart__tooltip-swatch"
                            style={{ background: entry.color }}
                          />
                          {entry.name}: {String(entry.value)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null
              }
            />
            {series.map((one, index) => {
              const shape = (props: StackShapeProps): React.JSX.Element | null => {
                const { x, y, width, height, index: position } = props;
                if (
                  x === undefined ||
                  y === undefined ||
                  width === undefined ||
                  height === undefined
                ) {
                  return null;
                }
                const datum = data[position ?? 0];
                const value =
                  datum?.segments.find((segment) => segment.key === one.key)?.value ?? 0;
                const name =
                  datum === undefined
                    ? undefined
                    : strings.charts.segment(one.label, datum.label, value);
                return (
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={seriesColor(index)}
                    className={seriesClass(index)}
                    role="img"
                    aria-label={name}
                  >
                    {name === undefined ? null : <title>{name}</title>}
                  </rect>
                );
              };
              return (
                <Bar
                  key={one.key}
                  dataKey={one.key}
                  stackId="a"
                  shape={shape}
                  isAnimationActive={false}
                  onClick={(_datum, barIndex) => {
                    if (onSelectSegment === undefined) {
                      return;
                    }
                    const picked = data[barIndex];
                    if (picked !== undefined) {
                      onSelectSegment(one.key, picked);
                    }
                  }}
                />
              );
            })}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
      <Legend entries={series.map((one, index) => ({ label: one.label, index }))} />
    </figure>
  );
}
